'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import api from '@/lib/axios';
import useAuthStore from '@/store/auth.store';
import { useHydration } from '@/store/useHydration';
import Input from '@/components/ui/Input';
import TelefonInput from '@/components/ui/TelefonInput';
import IlIlceSecici from '@/components/ui/IlIlceSecici';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { useAdresler, bosAdresForm, type Adres } from '@/hooks/useAdresler';
import { User, Mail, ShoppingBag, LogOut, CheckCircle, MapPin, Plus, Edit2, Trash2 } from 'lucide-react';

export default function HesabimPage() {
  const router = useRouter();
  const { girisYapildi, setKullanici, kullanici, cikisYap } = useAuthStore();
  const hydrated = useHydration();
  const [form, setForm] = useState({ ad: '', soyad: '', email: '', telefon: '' });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [basari, setBasari] = useState('');
  const [hata, setHata] = useState('');

  // Adres yönetimi
  const { adresler, yukleniyor: adresYukleniyor, kaydet: adresKaydet, sil: adresSil } = useAdresler(hydrated && girisYapildi);
  const [adresFormAcik, setAdresFormAcik] = useState(false);
  const [duzenleId, setDuzenleId] = useState<number | null>(null);
  const [adresForm, setAdresForm] = useState({ ...bosAdresForm });
  const [adresHatalar, setAdresHatalar] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!hydrated) return;
    if (!girisYapildi) { router.push('/giris'); return; }
    api.get('/kullanicilar/profil').then((res) => {
      setForm({ ad: res.data.ad || '', soyad: res.data.soyad || '', email: res.data.email || '', telefon: res.data.telefon || '' });
      setYukleniyor(false);
    }).catch(() => { router.push('/giris'); });
  }, [hydrated, girisYapildi]);

  const handleSubmit = async () => {
    setHata(''); setBasari(''); setKaydediliyor(true);
    try {
      const temizForm = { ...form, telefon: form.telefon.replace(/\s/g, '') };
      const res = await api.put('/kullanicilar/profil', temizForm);
      setKullanici(res.data);
      toast.success('Profil başarıyla güncellendi!');
    } catch (err: any) {
      setHata(err.response?.data?.message?.message || 'Güncelleme sırasında hata oluştu');
    } finally {
      setKaydediliyor(false);
    }
  };

  const adresFormAc = (adres?: Adres) => {
    if (adres) {
      setDuzenleId(adres.id);
      setAdresForm({
        baslik: adres.baslik || '', ad: adres.ad, soyad: adres.soyad,
        telefon: adres.telefon, il: adres.il, ilce: adres.ilce,
        mahalle: adres.mahalle || '', adres: adres.adres,
        posta_kodu: adres.posta_kodu || '', varsayilan: adres.varsayilan || false,
      });
    } else {
      setDuzenleId(null);
      setAdresForm({ ...bosAdresForm });
    }
    setAdresHatalar({});
    setAdresFormAcik(true);
  };

  const adresKaydetVeKapat = async () => {
    const h: Record<string, string> = {};
    if (!adresForm.ad) h.ad = 'Ad zorunludur';
    if (!adresForm.soyad) h.soyad = 'Soyad zorunludur';
    if (!adresForm.telefon) h.telefon = 'Telefon zorunludur';
    if (!adresForm.il) h.il = 'İl zorunludur';
    if (!adresForm.ilce) h.ilce = 'İlçe zorunludur';
    if (!adresForm.adres) h.adres = 'Adres zorunludur';
    if (Object.keys(h).length > 0) { setAdresHatalar(h); return; }

    const sonuc = await adresKaydet(adresForm, duzenleId);
    if (sonuc) {
      setAdresFormAcik(false);
      setDuzenleId(null);
      setAdresForm({ ...bosAdresForm });
      toast.success(duzenleId ? 'Adres güncellendi' : 'Adres eklendi');
    }
  };

  const adresSilVeGuncelle = async (id: number) => {
    const silindi = await adresSil(id);
    if (silindi) toast.success('Adres silindi');
  };

  if (!hydrated || yukleniyor) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-[var(--color-gray-200)] animate-pulse rounded-[var(--radius-xl)] h-64" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-[var(--color-gray-800)] mb-6">Hesabım</h1>

      {/* Kullanıcı Özeti */}
      <div className="bg-gradient-to-r from-[var(--color-primary)] to-blue-500 rounded-[var(--radius-xl)] p-6 text-white mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <span className="text-3xl font-bold">{kullanici?.ad?.[0]?.toUpperCase()}</span>
          </div>
          <div>
            <p className="text-xl font-bold">{kullanici?.ad} {kullanici?.soyad}</p>
            <p className="text-blue-100 text-sm">{kullanici?.email}</p>
          </div>
        </div>
      </div>

      {/* Hızlı Linkler */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link href="/siparislerim"
          className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-sm)] p-4 flex items-center gap-3 hover:shadow-[var(--shadow-md)] transition">
          <div className="w-10 h-10 bg-[var(--color-info-light)] rounded-full flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-[var(--color-primary)]" />
          </div>
          <div>
            <p className="font-semibold text-[var(--color-gray-800)] text-sm">Siparişlerim</p>
            <p className="text-xs text-[var(--color-gray-500)]">Tüm siparişlerin</p>
          </div>
        </Link>
        <button onClick={() => { cikisYap(); router.push('/'); }}
          className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-sm)] p-4 flex items-center gap-3 hover:shadow-[var(--shadow-md)] transition text-left">
          <div className="w-10 h-10 bg-[var(--color-error-light)] rounded-full flex items-center justify-center">
            <LogOut className="w-5 h-5 text-[var(--color-error)]" />
          </div>
          <div>
            <p className="font-semibold text-[var(--color-error)] text-sm">Çıkış Yap</p>
            <p className="text-xs text-[var(--color-gray-500)]">Güvenli çıkış</p>
          </div>
        </button>
      </div>

      {/* Profil Formu */}
      <div className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-md)] p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <User className="w-5 h-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-bold text-[var(--color-gray-800)]">Profil Bilgileri</h2>
        </div>

        {basari && (
          <div className="bg-[var(--color-success-light)] border border-[var(--color-success)] text-[var(--color-success)] rounded-[var(--radius-lg)] p-3 mb-4 text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />{basari}
          </div>
        )}
        {hata && (
          <div className="bg-[var(--color-error-light)] border border-[var(--color-error)] text-[var(--color-error)] rounded-[var(--radius-lg)] p-3 mb-4 text-sm">{hata}</div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Ad" value={form.ad} onChange={(e) => setForm({ ...form, ad: e.target.value })} leftIcon={<User className="w-4 h-4" />} />
            <Input label="Soyad" value={form.soyad} onChange={(e) => setForm({ ...form, soyad: e.target.value })} />
          </div>
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <TelefonInput value={form.telefon} onChange={(v) => setForm({ ...form, telefon: v })} />
          <Button fullWidth onClick={handleSubmit} loading={kaydediliyor}>Değişiklikleri Kaydet</Button>
        </div>
      </div>

      {/* Adreslerim */}
      <div className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-md)] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-bold text-[var(--color-gray-800)]">Adreslerim</h2>
          </div>
          {!adresFormAcik && (
            <button onClick={() => adresFormAc()}
              className="flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline font-medium">
              <Plus className="w-4 h-4" /> Yeni Adres
            </button>
          )}
        </div>

        {/* Adres Formu */}
        {adresFormAcik && (
          <div className="bg-[var(--color-gray-50)] border border-[var(--color-gray-200)] rounded-[var(--radius-xl)] p-4 mb-4 space-y-3">
            <p className="text-sm font-bold text-[var(--color-gray-700)]">
              {duzenleId ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
            </p>
            <Input label="Adres Başlığı (opsiyonel)" value={adresForm.baslik} onChange={(e) => setAdresForm(f => ({ ...f, baslik: e.target.value }))} placeholder="Ev, İş, Okul..." />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input label="Ad *" value={adresForm.ad} onChange={(e) => setAdresForm(f => ({ ...f, ad: e.target.value }))} />
                {adresHatalar.ad && <p className="text-xs text-[var(--color-error)] mt-1">{adresHatalar.ad}</p>}
              </div>
              <div>
                <Input label="Soyad *" value={adresForm.soyad} onChange={(e) => setAdresForm(f => ({ ...f, soyad: e.target.value }))} />
                {adresHatalar.soyad && <p className="text-xs text-[var(--color-error)] mt-1">{adresHatalar.soyad}</p>}
              </div>
              <div>
                <TelefonInput value={adresForm.telefon} onChange={(v) => setAdresForm(f => ({ ...f, telefon: v }))} hata={adresHatalar.telefon} zorunlu />
              </div>
              <Input label="Posta Kodu" value={adresForm.posta_kodu} onChange={(e) => setAdresForm(f => ({ ...f, posta_kodu: e.target.value }))} />
            </div>
            <IlIlceSecici
              il={adresForm.il} ilce={adresForm.ilce} mahalle={adresForm.mahalle}
              onIlChange={(v) => setAdresForm(f => ({ ...f, il: v, ilce: '', mahalle: '' }))}
              onIlceChange={(v) => setAdresForm(f => ({ ...f, ilce: v, mahalle: '' }))}
              onMahalleChange={(v) => setAdresForm(f => ({ ...f, mahalle: v }))}
            />
            {(adresHatalar.il || adresHatalar.ilce) && <p className="text-xs text-[var(--color-error)]">{adresHatalar.il || adresHatalar.ilce}</p>}
            <div>
              <label className="block text-sm font-medium text-[var(--color-gray-700)] mb-1">Açık Adres *</label>
              <textarea value={adresForm.adres} onChange={(e) => setAdresForm(f => ({ ...f, adres: e.target.value }))} rows={2}
                className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none ${adresHatalar.adres ? 'border-[var(--color-error)]' : 'border-[var(--color-gray-300)]'}`} />
              {adresHatalar.adres && <p className="text-xs text-[var(--color-error)] mt-1">{adresHatalar.adres}</p>}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={adresForm.varsayilan} onChange={(e) => setAdresForm(f => ({ ...f, varsayilan: e.target.checked }))} className="rounded" />
              <span className="text-[var(--color-gray-700)]">Varsayılan adres olarak kaydet</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={adresKaydetVeKapat} loading={adresYukleniyor} size="sm">Kaydet</Button>
              <Button variant="outline" size="sm" onClick={() => { setAdresFormAcik(false); setDuzenleId(null); }}>İptal</Button>
            </div>
          </div>
        )}

        {/* Adres Listesi */}
        {adresYukleniyor ? (
          <div className="space-y-2">
            {[1, 2].map(i => <div key={i} className="h-20 bg-[var(--color-gray-100)] animate-pulse rounded-[var(--radius-lg)]" />)}
          </div>
        ) : adresler.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-gray-400)]">
            <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Henüz adres eklenmemiş</p>
          </div>
        ) : (
          <div className="space-y-3">
            {adresler.map(adres => (
              <div key={adres.id}
                className="border border-[var(--color-gray-200)] rounded-[var(--radius-xl)] p-4 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {adres.baslik && (
                      <span className="text-xs font-bold text-[var(--color-primary)] bg-[var(--color-info-light)] px-2 py-0.5 rounded-full">
                        {adres.baslik}
                      </span>
                    )}
                    {adres.varsayilan && (
                      <span className="text-xs text-[var(--color-success)] font-medium bg-green-50 px-2 py-0.5 rounded-full">
                        ✓ Varsayılan
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-[var(--color-gray-800)] text-sm">{adres.ad} {adres.soyad}</p>
                  <p className="text-xs text-[var(--color-gray-500)]">{adres.telefon}</p>
                  <p className="text-xs text-[var(--color-gray-600)] mt-0.5">{adres.mahalle ? `${adres.mahalle} Mah. ` : ''}{adres.adres}</p>
                  <p className="text-xs text-[var(--color-gray-500)]">{adres.ilce} / {adres.il}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => adresFormAc(adres)}
                    className="p-2 text-[var(--color-gray-400)] hover:text-[var(--color-primary)] hover:bg-[var(--color-info-light)] rounded-[var(--radius-md)] transition">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => adresSilVeGuncelle(adres.id)}
                    className="p-2 text-[var(--color-gray-400)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-[var(--radius-md)] transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
